import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Upload, CheckCircle2, ShieldCheck, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CheckboxGroup from "@/components/forms/CheckboxGroup";
import LookupSelect from "@/components/forms/LookupSelect";
import { useLookups } from "@/contexts/LookupsContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const DOCUMENT_TYPES = ["cv", "id_card", "passport", "work_authorization"];

export default function CandidateProfileForm({ fetchUrl = "/profiles/candidate/me", saveUrl = "/profiles/candidate/me", showUploads = true, onSaved }) {
  const { t } = useTranslation();
  const lookups = useLookups();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProfile(null);
    api.get(fetchUrl).then(({ data }) => setProfile(data));
  }, [fetchUrl]);

  const update = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  const addMissionPreference = () =>
    setProfile((p) => ({ ...p, mission_preferences: [...(p.mission_preferences || []), { country: null, region: null, date_start: "", date_end: "" }] }));

  const updateMissionPreference = (idx, patch) =>
    setProfile((p) => ({
      ...p,
      mission_preferences: p.mission_preferences.map((pref, i) => (i === idx ? { ...pref, ...patch } : pref)),
    }));

  const removeMissionPreference = (idx) =>
    setProfile((p) => ({ ...p, mission_preferences: p.mission_preferences.filter((_, i) => i !== idx) }));


  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      delete payload.id;
      delete payload.user_id;
      delete payload.email;
      delete payload.photo_path;
      delete payload.documents;
      delete payload.is_complete;
      delete payload.updated_at;
      delete payload.is_tesa_ready;
      delete payload.tesa_missing_items;
      await api.put(saveUrl, payload);
      toast.success(t("candidateProfile.profileSaved"));
      const refreshed = await api.get(fetchUrl);
      setProfile(refreshed.data);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/profiles/candidate/me/photo", formData);
      setProfile((p) => ({ ...p, photo_path: data.photo_path }));
      toast.success(t("common.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  const handleDocumentUpload = async (docType, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("doc_type", docType);
    formData.append("file", file);
    try {
      const { data } = await api.post("/profiles/candidate/me/documents", formData);
      setProfile((p) => ({ ...p, documents: data.documents, is_tesa_ready: data.is_tesa_ready }));
      const refreshed = await api.get("/profiles/candidate/me");
      setProfile(refreshed.data);
      toast.success(t("common.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  if (!profile || !lookups) {
    return <div className="h-40 flex items-center justify-center text-sm text-[#2B2B2B]/50">{t("common.loading")}</div>;
  }

  const uploadedDoc = (type) => profile.documents?.find((d) => d.type === type);

  const TESA_ITEM_LABELS = {
    first_name: t("candidateProfile.firstName"), last_name: t("candidateProfile.lastName"),
    birth_date: t("candidateProfile.birthDate"), nationality: t("candidateProfile.nationality"),
    id_document: t("candidateProfile.idCard"), rib: t("candidateProfile.rib"),
    social_security_number: t("candidateProfile.socialSecurityNumber"),
    availability: t("candidateProfile.availabilitySection"), languages: t("candidateProfile.languagesSection"),
    experience_level: t("candidateProfile.experienceSection"),
  };
  const missingItems = profile.tesa_missing_items || [];

  return (
    <div className="space-y-10" data-testid="candidate-profile-form">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">{t("candidateProfile.title")}</h2>
        <Badge className={profile.is_complete ? "bg-[#34C759]/10 text-[#34C759] border-0" : "bg-[#dde165]/20 text-[#2B2B2B] border-0"}>
          {profile.is_complete ? t("candidateProfile.profileComplete") : t("candidateProfile.profileIncomplete")}
        </Badge>
      </div>

      {/* TESA compatibility */}
      <div className={`rounded-2xl border p-5 ${profile.is_tesa_ready ? "bg-[#34C759]/5 border-[#34C759]/20" : "bg-[#FF9500]/5 border-[#FF9500]/20"}`} data-testid="candidate-tesa-status-panel">
        {profile.is_tesa_ready ? (
          <div className="flex items-center gap-2 font-bold text-[#34C759]" data-testid="candidate-tesa-ready-status">
            <ShieldCheck className="h-5 w-5" /> {t("candidateProfile.tesaReadyBadge")}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 font-bold text-[#FF9500]" data-testid="candidate-tesa-missing-status">
              <AlertTriangle className="h-5 w-5" /> {t("candidateProfile.tesaMissingBadge", { count: missingItems.length })}
            </div>
            <p className="text-xs text-[#2B2B2B]/60 mt-2">
              {missingItems.map((key) => TESA_ITEM_LABELS[key]).filter(Boolean).join(" · ")}
            </p>
          </div>
        )}
      </div>

      {/* Photo */}
      {showUploads && (
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full bg-[#FAFAFA] border border-black/10 flex items-center justify-center overflow-hidden">
            {profile.photo_path ? (
              <img src={`${API}/profiles/files/${profile.photo_path}`} alt="" className="h-full w-full object-cover" />
            ) : (
              <Camera className="h-6 w-6 text-[#2B2B2B]/30" />
            )}
          </div>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#4ba3b7] hover:underline">
              <Upload className="h-4 w-4" /> {t("candidateProfile.photoUpload")}
            </span>
            <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={handlePhotoUpload} data-testid="candidate-photo-upload-input" />
          </label>
        </div>
      )}

      {/* Personal info */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.personalInfoSection")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("candidateProfile.firstName")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.first_name || ""} onChange={(e) => update("first_name", e.target.value)} data-testid="candidate-first-name-input" />
          </div>
          <div>
            <Label>{t("candidateProfile.lastName")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.last_name || ""} onChange={(e) => update("last_name", e.target.value)} data-testid="candidate-last-name-input" />
          </div>
          <div>
            <Label>{t("candidateProfile.phone")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="candidate-phone-input" />
          </div>
          <div>
            <Label>{t("candidateProfile.birthDate")}</Label>
            <Input type="date" className="h-12 rounded-xl mt-1.5" value={profile.birth_date || ""} onChange={(e) => update("birth_date", e.target.value)} data-testid="candidate-birth-date-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block">{t("candidateProfile.nationality")}</Label>
            <LookupSelect options={lookups.nationalities} value={profile.nationality} onChange={(v) => update("nationality", v)}
                          labelPrefix="lookups.nationalities" testId="candidate-nationality-select" />
          </div>
        </div>
      </section>

      {/* Languages */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.languagesSection")}</h3>
        <CheckboxGroup options={lookups.languages} value={profile.languages} onChange={(v) => update("languages", v)}
                       labelPrefix="lookups.languages" testIdPrefix="candidate-language" />
      </section>

      {/* Availability */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.availabilitySection")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("candidateProfile.availabilityStart")}</Label>
            <Input type="date" className="h-12 rounded-xl mt-1.5" value={profile.availability_start || ""} onChange={(e) => update("availability_start", e.target.value)} data-testid="candidate-availability-start-input" />
          </div>
          <div>
            <Label>{t("candidateProfile.availabilityEnd")}</Label>
            <Input type="date" className="h-12 rounded-xl mt-1.5" value={profile.availability_end || ""} onChange={(e) => update("availability_end", e.target.value)} data-testid="candidate-availability-end-input" />
          </div>
        </div>
      </section>

      {/* Countries wanted */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.countriesWantedSection")}</h3>
        <CheckboxGroup options={lookups.countries} value={profile.countries_wanted} onChange={(v) => update("countries_wanted", v)}
                       labelPrefix="lookups.countries" testIdPrefix="candidate-country-wanted" />
      </section>

      {/* Mission preferences: multiple (region + date range) entries */}
      <section>
        <h3 className="font-heading font-bold mb-1">{t("candidateProfile.missionPreferencesSection")}</h3>
        <p className="text-sm text-[#2B2B2B]/60 mb-4">{t("candidateProfile.missionPreferencesHint")}</p>
        <div className="space-y-3">
          {(profile.mission_preferences || []).map((pref, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end bg-[#FAFAFA] border border-black/10 rounded-xl p-4"
                 data-testid={`candidate-mission-pref-row-${idx}`}>
              <div>
                <Label className="mb-1.5 block text-xs">{t("candidateProfile.missionPreferenceCountry")}</Label>
                <LookupSelect options={lookups.countries} value={pref.country}
                              onChange={(v) => updateMissionPreference(idx, { country: v, region: null })}
                              labelPrefix="lookups.countries" testId={`candidate-mission-pref-${idx}-country-select`} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">{t("candidateProfile.missionPreferenceRegion")}</Label>
                <LookupSelect options={lookups.regions_by_country[pref.country] || []} value={pref.region}
                              onChange={(v) => updateMissionPreference(idx, { region: v })}
                              labelPrefix="lookups.regions" testId={`candidate-mission-pref-${idx}-region-select`} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">{t("candidateProfile.missionPreferenceDateStart")}</Label>
                <Input type="date" className="h-12 rounded-xl" value={pref.date_start || ""}
                       onChange={(e) => updateMissionPreference(idx, { date_start: e.target.value })}
                       data-testid={`candidate-mission-pref-${idx}-date-start-input`} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">{t("candidateProfile.missionPreferenceDateEnd")}</Label>
                <Input type="date" className="h-12 rounded-xl" value={pref.date_end || ""}
                       onChange={(e) => updateMissionPreference(idx, { date_end: e.target.value })}
                       data-testid={`candidate-mission-pref-${idx}-date-end-input`} />
              </div>
              <Button type="button" variant="outline" className="h-12 rounded-xl px-3" onClick={() => removeMissionPreference(idx)}
                      data-testid={`candidate-mission-pref-${idx}-remove-button`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" className="mt-3 rounded-xl gap-2" onClick={addMissionPreference}
                data-testid="candidate-mission-pref-add-button">
          <Plus className="h-4 w-4" /> {t("candidateProfile.addMissionPreference")}
        </Button>
      </section>

      {/* Permits & vehicle & housing */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.permitsSection")}</h3>
        <CheckboxGroup options={lookups.permits} value={profile.permits} onChange={(v) => update("permits", v)}
                       labelPrefix="lookups.permits" testIdPrefix="candidate-permit" />
        <label className="flex items-center gap-2 mt-4 cursor-pointer w-fit">
          <Checkbox checked={!!profile.has_vehicle} onCheckedChange={(v) => update("has_vehicle", !!v)} data-testid="candidate-has-vehicle-checkbox" />
          <span className="text-sm">{t("candidateProfile.hasVehicle")}</span>
        </label>
      </section>

      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.housingWanted")}</h3>
        <LookupSelect options={lookups.housing_options} value={profile.housing_wanted} onChange={(v) => update("housing_wanted", v)}
                      labelPrefix="lookups.housingOptions" testId="candidate-housing-wanted-select" />
      </section>

      {/* Experience & skills */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.experienceSection")}</h3>
        <LookupSelect options={lookups.experience_levels} value={profile.experience_level} onChange={(v) => update("experience_level", v)}
                      labelPrefix="lookups.experienceLevels" testId="candidate-experience-level-select" />
      </section>

      {/* Worker status & rate */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.workerStatusSection")}</h3>
        <LookupSelect options={lookups.worker_statuses} value={profile.worker_status} onChange={(v) => update("worker_status", v)}
                      labelPrefix="lookups.workerStatuses" testId="candidate-worker-status-select" />
      </section>

      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.rateSection")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">{t("candidateProfile.rateType")}</Label>
            <LookupSelect options={lookups.rate_types} value={profile.rate_type} onChange={(v) => update("rate_type", v)}
                          labelPrefix="lookups.rateTypes" testId="candidate-rate-type-select" />
          </div>
          <div>
            <Label>{t("candidateProfile.rateAmount")}</Label>
            <Input type="number" step="0.01" className="h-12 rounded-xl mt-1.5" value={profile.rate_amount ?? ""}
                   onChange={(e) => update("rate_amount", e.target.value === "" ? null : Number(e.target.value))} data-testid="candidate-rate-amount-input" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.skillsSection")}</h3>
        <CheckboxGroup options={lookups.skills} value={profile.skills} onChange={(v) => update("skills", v)}
                       labelPrefix="lookups.skills" testIdPrefix="candidate-skill" />
      </section>

      {/* Description */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.descriptionSection")}</h3>
        <Textarea rows={4} className="rounded-xl" placeholder={t("candidateProfile.descriptionPlaceholder")}
                  value={profile.description || ""} onChange={(e) => update("description", e.target.value)} data-testid="candidate-description-textarea" />
      </section>

      {/* Documents */}
      <section>
        <h3 className="font-heading font-bold mb-4">{t("candidateProfile.documentsSection")}</h3>
        {showUploads && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DOCUMENT_TYPES.map((docType) => {
              const doc = uploadedDoc(docType);
              return (
                <label key={docType} className="flex items-center justify-between gap-2 rounded-xl border border-black/10 bg-[#FAFAFA] px-4 py-3 cursor-pointer hover:border-[#4ba3b7]/40">
                  <span className="text-sm font-medium">{t(`candidateProfile.${docType === "id_card" ? "idCard" : docType === "work_authorization" ? "workAuthorization" : docType}`)}</span>
                  {doc ? <CheckCircle2 className="h-5 w-5 text-[#34C759]" /> : <Upload className="h-4 w-4 text-[#2B2B2B]/40" />}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => handleDocumentUpload(docType, e)} data-testid={`candidate-document-${docType}-upload-input`} />
                </label>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>{t("candidateProfile.rib")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.rib || ""} onChange={(e) => update("rib", e.target.value)} data-testid="candidate-rib-input" />
          </div>
          <div>
            <Label>{t("candidateProfile.socialSecurityNumber")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.social_security_number || ""} onChange={(e) => update("social_security_number", e.target.value)} data-testid="candidate-ssn-input" />
          </div>
        </div>
      </section>

      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto h-12 px-8 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="candidate-save-profile-button">
        {saving ? t("common.loading") : t("candidateProfile.saveProfile")}
      </Button>
    </div>
  );
}
